/**
 * setAttributeGuard — stops one bad attribute value from blanking the app.
 *
 * WHY THIS EXISTS:
 *   Vue 2 writes attributes during its patch phase (platforms/web/runtime/
 *   modules/attrs.js) with a bare `el.setAttribute(key, value)` and no
 *   try/catch anywhere up the call stack. `setAttribute` coerces its value
 *   with ToString, so a value that cannot be coerced throws:
 *
 *     - a Symbol                     -> TypeError, cannot convert to string
 *     - an object with no prototype  -> TypeError, no toString to call
 *     - an object whose toString()   -> whatever that getter threw
 *       or a valueOf() throws
 *     - a malformed attribute name   -> InvalidCharacterError (DOMException)
 *
 *   Thrown from inside patch, the exception unwinds the whole render. The
 *   component tree is left half-built, `#app` stays empty and the user gets a
 *   blank white page with a single console line that points at Vue internals
 *   rather than at the binding that caused it.
 *
 *   This downgrades that to a logged error naming the offending attribute and
 *   the element it was being written to. The attribute is skipped; everything
 *   else on the page still renders. A missing attribute is a visual defect on
 *   one element — a thrown one is an outage for the whole screen, and this is
 *   software radiologists read studies on.
 *
 * SCOPE:
 *   Deliberately narrow. Only setAttribute/setAttributeNS are wrapped, only
 *   their throw path is altered, and a successful call is passed straight
 *   through to the native method with its own return value. This is a net for
 *   a specific failure, not a general "ignore DOM errors" switch.
 *
 * Installed from main.js before anything mounts — a guard added after the
 * first render has already missed the render it was meant to protect.
 */

// Native methods are patched once per document. Vue's HMR and the unit tests
// both re-import this module, and wrapping an already-wrapped method would
// nest the try/catch a level deeper on every reload.
let installed = false;

// Attribute names already reported. A binding that produces an uncoercible
// value produces it on every re-render of its component, so without this the
// console takes hundreds of identical lines a minute and buries the rest.
const reported = new Set();

// Hard ceiling on distinct reports, so a pathological render cannot turn the
// console into the problem.
const MAX_REPORTS = 50;

/**
 * The outerHTML of the element with its children stripped — enough to identify
 * which template produced it (class names, alt text) without dumping a subtree.
 */
function describeMarkup(element) {
  try {
    if (!element || !element.cloneNode) return "(unavailable)";
    const clone = element.cloneNode(false);
    const html = clone.outerHTML || "(unavailable)";
    return html.length > 200 ? html.slice(0, 200) + "…" : html;
  } catch (e) {
    return "(unavailable)";
  }
}

/**
 * Recover the string a caller almost certainly meant.
 *
 * A webpack asset module, and the resolution of a dynamic import(), are module
 * NAMESPACE objects: { default: "img/foo.1a2b3c4d.svg" }. Passing the namespace
 * where its default was meant is the single most common way an <img src> binding
 * ends up unwritable — `require("@/assets/x.svg")` in a :src, or a missing
 * `.default` on an import. The intended value is unambiguous, so it is written
 * rather than dropped: a skipped src is a broken image in a radiology worklist.
 *
 * Returns undefined for anything else, which is then skipped as before.
 */
function unwrapModuleDefault(value) {
  if (!value || typeof value !== "object") return undefined;

  let fallback;
  try {
    fallback = value.default;
  } catch (e) {
    return undefined; // getter threw - nothing safe to use
  }

  const type = typeof fallback;
  return type === "string" || type === "number" ? fallback : undefined;
}

/**
 * A short, safe description of the value that could not be written.
 *
 * String() is exactly what threw for object values, so it cannot be used here.
 * Constructor name and own keys are read defensively — a Proxy can throw from
 * its own traps, and this must never become a second source of errors.
 */
function describeValue(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";

  const type = typeof value;

  if (type === "symbol") {
    try {
      return value.toString();
    } catch (e) {
      return "Symbol";
    }
  }
  if (type === "function") {
    try {
      return `function ${value.name || "(anonymous)"}`;
    } catch (e) {
      return "function";
    }
  }
  if (type !== "object") {
    try {
      return String(value);
    } catch (e) {
      return type;
    }
  }

  let name = "Object";
  try {
    if (value.constructor && value.constructor.name) name = value.constructor.name;
  } catch (e) {
    // Null-prototype objects have no constructor; Proxy traps may throw.
  }

  try {
    // Five keys is enough to recognise which binding this is without turning
    // one console line into a wall of text.
    const keys = Object.keys(value);
    if (keys.length) {
      const shown = keys.slice(0, 5).join(", ");
      return `${name} { ${shown}${keys.length > 5 ? ", …" : ""} }`;
    }
  } catch (e) {
    // Not enumerable — the constructor name alone still identifies it.
  }

  return name;
}

/** Enough of the element to find it in the template that produced it. */
function describeElement(element) {
  try {
    if (!element || !element.tagName) return "(unknown element)";

    let out = element.tagName.toLowerCase();
    if (element.id) out += `#${element.id}`;

    // className is a SVGAnimatedString on SVG elements, not a string.
    const className = element.getAttribute && element.getAttribute("class");
    if (className) out += `.${String(className).trim().split(/\s+/).join(".")}`;

    return out;
  } catch (e) {
    return "(unknown element)";
  }
}

function report(methodName, element, attributeName, value, error, outcome) {
  // Keyed on the VALUE as well as the attribute. Keying on the attribute alone
  // collapsed every failing <img src> in the app into a single console line,
  // which named the failure but not which of them produced it.
  const key = `${methodName}:${attributeName}:${describeValue(value)}`;
  if (reported.has(key)) return;
  if (reported.size >= MAX_REPORTS) return;
  reported.add(key);

  // eslint-disable-next-line no-console
  if (typeof console === "undefined" || typeof console.error !== "function") return;

  try {
    // eslint-disable-next-line no-console
    console.error(
      `[setAttributeGuard] ${outcome} ${methodName}("${attributeName}") on ` +
        `<${describeElement(element)}> — value was ${describeValue(value)}. ` +
        `Element: ${describeMarkup(element)}`,
      error
    );
  } catch (e) {
    // A locked-down console must not take the page down with it.
  }
}

/**
 * Wrap one method on a prototype so a throw becomes a logged, skipped write.
 *
 * `valueIndex` differs between the two methods: setAttribute(name, value)
 * carries the attribute name at 0, setAttributeNS(ns, name, value) at 1.
 */
function wrap(prototype, methodName, nameIndex) {
  const original = prototype[methodName];

  if (typeof original !== "function" || original.__quickradGuarded) return;

  function guarded() {
    try {
      return original.apply(this, arguments);
    } catch (error) {
      // The name itself can be a value that fails to coerce, so it is read
      // through the same defensive path as the value.
      const label = describeValue(arguments[nameIndex]);
      const value = arguments[nameIndex + 1];
      const recovered = unwrapModuleDefault(value);

      if (recovered !== undefined) {
        try {
          const args = Array.prototype.slice.call(arguments);
          args[nameIndex + 1] = recovered;
          const result = original.apply(this, args);
          report(methodName, this, label, value, error, "Recovered");
          return result;
        } catch (retryError) {
          // Fall through and skip, as if no recovery had been attempted.
        }
      }

      report(methodName, this, label, value, error, "Skipped");
      return undefined;
    }
  }

  guarded.__quickradGuarded = true;

  try {
    prototype[methodName] = guarded;
  } catch (e) {
    // A frozen or sealed prototype (some embedded/kiosk browsers) — the app
    // behaves exactly as it did before this module existed.
  }
}

/**
 * Idempotent. Safe to call more than once; only the first call patches.
 *
 * A no-op outside the browser, so unit tests and any SSR-style import can pull
 * main.js in without a DOM present.
 */
export function installSetAttributeGuard() {
  if (installed) return;
  if (typeof Element === "undefined" || !Element.prototype) return;

  installed = true;

  wrap(Element.prototype, "setAttribute", 0);
  wrap(Element.prototype, "setAttributeNS", 1);
}

// Test seam: unit tests need a clean module between cases.
export function __resetForTests() {
  installed = false;
  reported.clear();
}

export default installSetAttributeGuard;
